import React, { createContext, useState } from 'react';
import { JConfirmationBar } from '@atoms/JConfirmationBar';
import useJDialog from '@/hooks/useJDialog';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import { JButton } from '@atoms/JButton';

interface JConfirmationBarContextProps {
  show: (props: ShowJConfirmationBarProps) => void;
  close: () => void;
}

interface JConfirmationBarData {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

export interface ShowJConfirmationBarProps {
  onClose: () => void;
  onConfirm: () => void;
}

export const JConfirmationBarContext = createContext<
  JConfirmationBarContextProps | undefined
>(undefined);

export const JConfirmationBarProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [jConfirmationBarData, setJConfirmationBarData] =
    useState<JConfirmationBarData | null>(null);
  const { showJDialog, closeJDialog } = useJDialog();
  const { t } = useTranslation();
  const showJConfirmationBar = ({
    onClose,
    onConfirm,
  }: ShowJConfirmationBarProps) => {
    setJConfirmationBarData({ open: true, onClose, onConfirm });
  };

  const closeJConfirmationBar = () => {
    setJConfirmationBarData(null);
    jConfirmationBarData?.onClose();
    closeJDialog();
  };

  const openCancelDialog = () => {
    showJDialog(
      t('QUIT'),
      <CancelDialog hideNotificationBar={closeJConfirmationBar} />,
    );
  };

  const handleOnConfirmAndClose = () => {
    jConfirmationBarData?.onConfirm();
    closeJDialog();
  };
  return (
    <JConfirmationBarContext.Provider
      value={{
        close: closeJConfirmationBar,
        show: ({ onClose, onConfirm }) =>
          showJConfirmationBar({ onClose, onConfirm }),
      }}
    >
      {children}
      {jConfirmationBarData?.open && (
        <JConfirmationBar
          onConfirm={handleOnConfirmAndClose}
          onClose={openCancelDialog}
        />
      )}
    </JConfirmationBarContext.Provider>
  );
};

interface CancelDialogProps {
  hideNotificationBar: () => void;
}

const CancelDialog = ({ hideNotificationBar }: CancelDialogProps) => {
  const { t } = useTranslation();
  const { closeJDialog } = useJDialog();
  return (
    <>
      <Typography>
        {t('YOU_MADE_MODIFICATIONS_BUT_DID_NOT_SAVE_ARE_YOU_SURE')}
      </Typography>

      <Stack direction={'row'} gap={2} mt={2}>
        <JButton
          onClick={hideNotificationBar}
          variant="contained"
          color="error"
        >
          {t('QUIT')}
        </JButton>
        <JButton onClick={closeJDialog} variant="contained" color="info">
          {t('CANCEL')}
        </JButton>
      </Stack>
    </>
  );
};
